// The design system's shared words. Every user-visible string in src/mc comes from a defineLabels() table or a
// template(): each entry quotes the line of his glossary (or of the vendored design-system excerpts) it is taken
// from, and labels.test.mjs proves the quote is verbatim, the text sits inside it, nothing is forbidden, and a word
// still waiting for his confirmation is marked pending. A group writes its own src/mc/gN/labels.ts with the same
// two functions and never edits this file.

import { forbiddenHits } from "./forbidden";
import { ACCESS_WORDS, ACCESS_WORDS_QUOTE } from "./glossary.generated";

/** Where a quote comes from: a glossary section (G §n, G §23.n), an open question or note, a DS section or ruling. */
export type LabelSource =
  | `G §${number}`
  | `Q-E${number}`
  | `T-${number}`
  | `DC-${number}`
  | `DS §${string}`;

export type LabelStatus = "his" | "pending";

export interface LabelDef {
  readonly text: string;
  readonly source: LabelSource;
  /** The verbatim line the text is taken from (whitespace, `*` and backticks are ignored when it is checked). */
  readonly quote: string;
  readonly status: LabelStatus;
}

export interface RegisteredLabel extends LabelDef {
  readonly key: string;
  readonly kind: "label" | "template";
}

const registry: RegisteredLabel[] = [];

/** Every label and template defined so far, in definition order (tests and the gallery's pending panel read it). */
export function registeredLabels(): readonly RegisteredLabel[] {
  return registry;
}

/** Registers the defs and returns {key: text}, typed with each literal text. */
export function defineLabels<const T extends Readonly<Record<string, LabelDef>>>(defs: T): { readonly [K in keyof T]: T[K]["text"] } {
  const out: Record<string, string> = {};
  for (const [key, def] of Object.entries(defs)) {
    registry.push({ key, kind: "label", text: def.text, source: def.source, quote: def.quote, status: def.status });
    out[key] = def.text;
  }
  return Object.freeze(out) as { readonly [K in keyof T]: T[K]["text"] };
}

/** The slot names of a template text: "Due in {n} business days" → "n". */
export type TemplateSlots<S extends string> = S extends `${string}{${infer K}}${infer Rest}` ? K | TemplateSlots<Rest> : never;

export interface Template<S extends string> {
  (slots: Readonly<Record<TemplateSlots<S>, string | number>>): string;
  readonly text: S;
}

/** A sentence with {slot} placeholders; components fill it and never concatenate words. */
export function template<const D extends LabelDef>(def: D): Template<D["text"]> {
  registry.push({ key: def.text, kind: "template", text: def.text, source: def.source, quote: def.quote, status: def.status });
  const fill = (slots: Readonly<Record<string, string | number>>): string =>
    def.text.replace(/\{([^}]+)\}/g, (whole, name: string) => (name in slots ? String(slots[name]) : whole));
  return Object.assign(fill, { text: def.text }) as Template<D["text"]>;
}

/** Placeholders ({x}, …) collapse to {} so a template and his "…" line compare equal. */
export function labelKey(text: string): string {
  return text.replace(/\{[^}]*\}/g, "{}").replace(/…/g, "{}").replace(/\s+/g, " ").trim();
}

/** A filled template matches this pattern (each slot is any non-empty run). */
export function templatePattern(text: string): RegExp {
  const parts = text.split(/\{[^}]+\}/).map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`^${parts.join(".+?")}$`, "u");
}

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const;

// ---- roles (G §3) --------------------------------------------------------------------------------------------------

export const ROLE_LABELS = /* @__PURE__ */ defineLabels({
  hm: his("Hiring Manager"),
  hmAbbr: his("HM"),
  recruiter: his("Recruiter"),
  coordinator: his("Coordinator"),
  hrbp: his("HR Business Partner"),
  hrbpAbbr: his("HRBP"),
  compensation: his("Compensation"),
  compensationAbbr: his("Comp"),
  interviewer: his("Interviewer"),
  taManager: his("TA Manager"),
  taManagerAbbr: his("TA Mgr"),
  // DC-228 as amended 12 Oct 05:29: one Admin (the 8 Oct TA Admin and HR Systems Admin fold back into it; HR Operations
  // folds into Coordinator; the TA Manager stays a role of its own). The role name, the matrix column head and the seat
  // chip; never a nav destination (Settings, DC-32).
  admin: his("Admin"),
});

/** The eight seats of the master permission matrix (DC-228 as amended 05:29), in the matrix's column order: the BRD's
 *  order with the dropped columns removed and the Admin last (cockpit ruling 12 Oct 06:10). */
export const SEATS = ["hm", "recruiter", "coordinator", "hrbp", "compensation", "interviewer", "taManager", "admin"] as const;
export type SeatKey = (typeof SEATS)[number];

// ---- navigation (DC-32; a proposal until Q-E9) ---------------------------------------------------------------------

const DC32 = "master set Home · Action Center · Requisitions · Candidates · Pipeline · Interviews · Talent community · Approvals · Reports · Settings, trimmed per seat; Coordinator adds **Coordinator Workbench**, Compensation adds **Compensation queue**, the Interviewer gets Home · Action Center · Interviews · Debriefs · Settings.";

export const NAV_ITEM_LABELS = /* @__PURE__ */ defineLabels({
  home: his("Home"),
  actionCenter: his("Action Center"),
  coordinatorWorkbench: his("Coordinator Workbench"),
  compensationQueue: his("Compensation queue"),
  requisitions: his("Requisitions"),
  candidates: his("Candidates"),
  pipeline: his("Pipeline"),
  interviews: his("Interviews"),
  debriefs: his("Debriefs"),
  talentCommunity: his("Talent community"),
  approvals: his("Approvals"),
  reports: his("Reports"),
  settings: his("Settings"),
});

export type NavKey = keyof typeof NAV_ITEM_LABELS;

/** The master order: the columns of the DC-32 table. Every seat's set keeps it. */
export const NAV_ORDER: readonly NavKey[] = [
  "home", "actionCenter", "coordinatorWorkbench", "compensationQueue", "requisitions", "candidates", "pipeline",
  "interviews", "debriefs", "talentCommunity", "approvals", "reports", "settings",
];

/**
 * The scope a seat holds on an item, where the DC-32 table qualifies the dot (data, not a drawn label). One seat may
 * hold more than one on an item: the Admin's Settings is "console + System Admin" (DC-228).
 */
export type NavScope = "own" | "own reqs" | "view" | "console" | "System Admin";
export interface NavEntry {
  readonly key: NavKey;
  readonly scope?: NavScope | readonly NavScope[];
}

const n = (key: NavKey, scope?: NavScope | readonly NavScope[]): NavEntry => (scope ? { key, scope } : { key });

/** Per seat, the DC-32 table row by row. */
export const NAV_BY_SEAT: Readonly<Record<SeatKey, readonly NavEntry[]>> = {
  hm: [n("home"), n("actionCenter"), n("requisitions", "own"), n("pipeline", "own"), n("interviews"), n("approvals"), n("reports", "own reqs"), n("settings")],
  recruiter: [n("home"), n("actionCenter"), n("coordinatorWorkbench"), n("requisitions"), n("candidates"), n("pipeline"), n("interviews"), n("talentCommunity"), n("reports"), n("settings")],
  coordinator: [n("home"), n("actionCenter"), n("coordinatorWorkbench"), n("requisitions"), n("candidates"), n("interviews"), n("talentCommunity"), n("reports"), n("settings")],
  hrbp: [n("home"), n("actionCenter"), n("requisitions"), n("candidates"), n("pipeline"), n("talentCommunity", "view"), n("approvals"), n("reports"), n("settings")],
  taManager: [n("home"), n("actionCenter"), n("coordinatorWorkbench", "view"), n("requisitions"), n("candidates"), n("pipeline"), n("interviews"), n("talentCommunity"), n("approvals"), n("reports"), n("settings")],
  compensation: [n("home"), n("actionCenter"), n("compensationQueue"), n("requisitions"), n("reports"), n("settings")],
  interviewer: [n("home"), n("actionCenter"), n("interviews"), n("debriefs"), n("settings")],
  admin: [n("home"), n("actionCenter"), n("coordinatorWorkbench", "view"), n("requisitions"), n("candidates"), n("pipeline"), n("interviews"), n("talentCommunity"), n("approvals"), n("reports"), n("settings", ["console", "System Admin"])],
};

/** The items that carry a count badge. */
export const NAV_BADGES: readonly NavKey[] = ["actionCenter", "approvals", "coordinatorWorkbench"];

/** The ≤ 768 px bottom tab bar: five items. Coordinator takes Coordinator Workbench for Interviews. */
export const NAV_MOBILE_DEFAULT: readonly NavKey[] = ["home", "actionCenter", "requisitions", "candidates", "interviews"];
export const NAV_MOBILE_BY_SEAT: Readonly<Partial<Record<SeatKey, readonly NavKey[]>>> = {
  coordinator: ["home", "actionCenter", "requisitions", "candidates", "coordinatorWorkbench"],
  interviewer: ["home", "actionCenter", "interviews", "debriefs", "settings"],
};

/** A multi-role person gets the union, once, in the master order. */
export function navFor(seats: readonly SeatKey[]): NavKey[] {
  const held = new Set(seats.flatMap((seat) => NAV_BY_SEAT[seat].map((entry) => entry.key)));
  return NAV_ORDER.filter((key) => held.has(key));
}

/**
 * The five bottom-bar items for a set of seats: the seat's DC-32 five (Coordinator's, else the Interviewer's when
 * that is the only seat, else the default), keeping only items the seats hold, topped up from the union in master
 * order. A seat without some of the default five (HM has no Candidates) is a default taken here, not a ruling.
 */
export function mobileNavFor(seats: readonly SeatKey[]): NavKey[] {
  const union = navFor(seats);
  const preferred = seats.includes("coordinator")
    ? NAV_MOBILE_BY_SEAT.coordinator ?? NAV_MOBILE_DEFAULT
    : seats.length > 0 && seats.every((seat) => seat === "interviewer")
      ? NAV_MOBILE_BY_SEAT.interviewer ?? NAV_MOBILE_DEFAULT
      : NAV_MOBILE_DEFAULT;
  const chosen = preferred.filter((key) => union.includes(key));
  for (const key of union) if (chosen.length < 5 && !chosen.includes(key)) chosen.push(key);
  return NAV_ORDER.filter((key) => chosen.includes(key));
}

// ---- verbs (G §17) and access words (G §16) ------------------------------------------------------------------------

export const ACTION_LABELS = /* @__PURE__ */ defineLabels({
  create: his("Create"),
  certify: his("Certify"),
  approve: his("Approve"),
  return: his("Return"),
  /** The refusal of a hold, cancel or reopen request (Return stays on the Hiring Request's decisions). */
  reject: his("Reject"),
  createPost: his("Create/post"),
  assign: his("Assign"),
  reassign: his("Reassign"),
  requestReassignment: his("Request reassignment"),
  finalize: his("Finalize"),
  submit: his("Submit"),
  advance: his("Advance"),
  hold: his("Hold"),
  pass: his("Pass"),
  select: his("Select"),
  schedule: his("Schedule"),
  reschedule: his("Reschedule"),
  cancel: his("Cancel"),
  proposeSlots: his("Propose slots"),
  review: his("Review"),
  release: his("Release"),
  accept: his("Accept"),
  decline: his("Decline"),
  revoke: his("Revoke"),
  regenerate: his("Regenerate"),
  reveal: his("Reveal"),
  discuss: his("Discuss"),
  reviseOwnScore: his("Revise own score"),
  align: his("Align"),
  recordOutcome: his("Record outcome"),
  prepare: his("Prepare"),
  extendVerbalOffer: his("Extend verbal offer"),
  sendWritten: his("Send written"),
  counter: his("Counter"),
  revise: his("Revise"),
  disposition: his("Disposition"),
  validateHandoff: his("Validate handoff"),
  confirmHris: his("Confirm HRIS"),
  complete: his("Complete"),
  designate: his("Designate"),
  requestHold: his("Request hold"),
  upload: his("Upload"),
  validate: his("Validate"),
  parse: his("Parse"),
  reviewEdit: his("Review/edit"),
  duplicateCheck: his("Duplicate check"),
  save: his("Save"),
  merge: his("Merge"),
  recover: his("Recover"),
  drillDown: his("Drill down"),
  export: his("Export"),
  saveView: his("Save view"),
  retry: his("Retry"),
});

/**
 * His access words, verbatim as the disabled-control descriptions and the permission console's labels. A word that
 * is forbidden as a whole label would be left out; since DC-228 none is (the bare Admin is the Admin column's cell word
 * and only a nav destination may not read it), so every one of his words is here.
 */
export type AccessWord = (typeof ACCESS_WORDS)[number];
export const ACCESS_LABELS = /* @__PURE__ */ defineLabels(
  Object.fromEntries(
    ACCESS_WORDS.filter((word) => forbiddenHits(word).length === 0).map((word) => [word, his(word)]),
  ) as Record<AccessWord, LabelDef>,
) as Readonly<Record<AccessWord, AccessWord>>;

// ---- SLA (DC-19; G §10, §11, §14) ----------------------------------------------------------------------------------

const TIERS = "the DC-19 tiers **Breach — dashboard risk · Near-breach · Reminder · Due**";
const DC19 = "50 % reminder → `warn` *Reminder*; near-breach → `flag` *Near-breach*; breach → `red` *Overdue* (*Past Due* for scorecards, his word)";

/** The four tiers of an SLA clock, most severe first. */
export const SLA_TIER_LABELS = /* @__PURE__ */ defineLabels({
  breach: his("Breach — dashboard risk"),
  nearBreach: his("Near-breach"),
  reminder: his("Reminder"),
  due: his("Due"),
});

/** The words a due pill carries as the tier rises. */
export const SLA_PILL_LABELS = /* @__PURE__ */ defineLabels({
  reminder: his("Reminder"),
  nearBreach: his("Near-breach"),
  overdue: his("Overdue"),
  pastDue: his("Past Due"),
});

export const SLA_LABELS = /* @__PURE__ */ defineLabels({
  scorecardDue: his("Scorecard Due"),
  scorecardSla: his("24-hour scorecard SLA"),
  standardSla: his("Standard SLA: 24 hours"),
  expeditedSla: his("Expedited SLA: 4 business hours"),
  emergency: his("Emergency: immediate review"),
  activeTime: his("Active Time"),
  calendarTime: his("Calendar Time"),
  daysOnHold: his("Days on Hold"),
});

// ---- ROOTS (G §6; DS #23) and levels (G §6, §11, §22) --------------------------------------------------------------

const ROOTS = "The five (Email): Respect · Ownership · Opportunity · Together · Service.";
const ROOTS_ABBR = "RES · OWN · OPP · TOG · SER (Respect · Ownership · Opportunity · Together · Service)";

export const ROOTS_LABELS = /* @__PURE__ */ defineLabels({
  roots: his("ROOTS"),
  respect: his("Respect"),
  ownership: his("Ownership"),
  opportunity: his("Opportunity"),
  together: his("Together"),
  service: his("Service"),
});

export const ROOTS_ABBR_LABELS = /* @__PURE__ */ defineLabels({
  respect: his("RES"),
  ownership: his("OWN"),
  opportunity: his("OPP"),
  together: his("TOG"),
  service: his("SER"),
});

export type RootsValue = keyof typeof ROOTS_ABBR_LABELS;
export const ROOTS_ORDER: readonly RootsValue[] = ["respect", "ownership", "opportunity", "together", "service"];

const LEVELS = "Levels he names: \"Sr Director and below\", \"VP+\"; Email cards \"Manager & Below\", \"Director\", \"VP+\".";

/** The framework's display names (his email's level cards). */
export const LEVEL_LABELS = /* @__PURE__ */ defineLabels({
  managerAndBelow: his("Manager & Below"),
  director: his("Director"),
  vpPlus: his("VP+"),
});

/** The decision-logic bands (his two) and the words the level rule draws. */
export const LEVEL_RULE_LABELS = /* @__PURE__ */ defineLabels({
  srDirectorAndBelow: his("Sr Director and below"),
  vpPlus: his("VP+"),
  concernFlag: his("Concern flag"),
  srDirectorRule: his("Any No Hire or two ratings of 2 or below creates a concern flag"),
  vpRule: his("Any No Hire or any rating of 2 or below is No Hire"),
});

/** The domain level enum (V1 and the backend) to the display name and the decision band. */
export type Level = "manager_and_below" | "director_srdirector" | "vp_and_above";
export const LEVEL_DISPLAY: Readonly<Record<Level, string>> = {
  manager_and_below: LEVEL_LABELS.managerAndBelow,
  director_srdirector: LEVEL_LABELS.director,
  vp_and_above: LEVEL_LABELS.vpPlus,
};
export const LEVEL_BAND: Readonly<Record<Level, string>> = {
  manager_and_below: LEVEL_RULE_LABELS.srDirectorAndBelow,
  director_srdirector: LEVEL_RULE_LABELS.srDirectorAndBelow,
  vp_and_above: LEVEL_RULE_LABELS.vpPlus,
};

// ---- rule sentences ------------------------------------------------------------------------------------------------

export const RULE_LABELS = /* @__PURE__ */ defineLabels({
  withdrawalRequires: his("Withdrawal requires reason/stage/date."),
  noBlockWhenNotRequired: his("No block when not required."),
  healthInformsAttention: his("Health informs attention; it does not make employment decisions."),
  slaUsesActiveTime: his("SLA uses Active Time; Calendar Time remains visible."),
  interviewersNoAccess: his("Interviewers have no access at this stage."),
  screenNeedsApplication: his("Screen cannot be scheduled before candidate application exists."),
  policyPrivacyReview: his("Subject to final policy/privacy review."),
  sensitiveReferenceData: his("Sensitive reference data is permission-protected."),
  noFinalizationMissing: his("No finalization with missing required scorecards/attendees."),
  failedSyncRetryable: his("Failed sync is visible and retryable."),
});

// ---- the state model's own words (DS §2.1, §2.3) -------------------------------------------------------------------

const ERROR_ROW = "with the one-line error, **Retry**, and an **Error details** disclosure (Correlation ID in mono · status · retries · error details · last attempt)";

/** The Error standard (ErrorStrip): the action, the disclosure and its key/value rows. */
export const ERROR_LABELS = /* @__PURE__ */ defineLabels({
  retry: his("Retry"),
  errorDetails: his("Error details"),
  correlationId: his("Correlation ID"),
  status: his("status"),
  retries: his("retries"),
  details: his("error details"),
  lastAttempt: his("last attempt"),
});

export const COMMON_LABELS = /* @__PURE__ */ defineLabels({
  required: his("Required"),
  optional: his("Optional"),
  candidateId: his("Candidate ID"),
  copyCandidateId: his("Copy Candidate ID"),
  /** Q-E15 now lists *Print* among the controls whose word sits outside his three sources. */
  print: pending("Print"),
});

// ---- status chains (G §5 – §12) ------------------------------------------------------------------------------------

export const REQUEST_STATUS = /* @__PURE__ */ defineLabels({
  draft: his("Draft"),
  pendingHmCertification: his("Pending HM Certification"),
  hmCertified: his("HM Certified"),
  pendingHrbpApproval: his("Pending HRBP Approval"),
  approved: his("Approved"),
  returned: his("Returned"),
  dormant: his("Dormant"),
});

const REQ_CHAIN = "Chain \"Request Approved -> Req Creation Pending -> Created -> Posted.\"";
const REQ_STATES = "\"Additional states: On Hold, Cancellation Pending/Cancelled, Reopen Pending/Reopened.\"";
const CLOSE_CHAIN = "\"Statuses / workflow: Offer Accepted -> Ready for HRIS -> HRIS Hired -> Filled -> Archived.\"";

export const REQUISITION_STATUS = /* @__PURE__ */ defineLabels({
  reqCreationPending: his("Req Creation Pending"),
  created: his("Created"),
  posted: his("Posted"),
  onHold: his("On Hold"),
  cancellationPending: his("Cancellation Pending"),
  cancelled: his("Cancelled"),
  reopenPending: his("Reopen Pending"),
  reopened: his("Reopened"),
  offerAccepted: his("Offer Accepted"),
  recruitingComplete: his("Recruiting complete"),
  readyForHris: his("Ready for HRIS"),
  hrisHired: his("HRIS Hired"),
  filled: his("Filled"),
  archived: his("Archived"),
});

const ASSIGN_CHAIN = "chain \"Recommended -> Pending TA Manager -> Assigned.\"";
export const RECRUITER_ASSIGNMENT_STATUS = /* @__PURE__ */ defineLabels({
  recommended: his("Recommended"),
  pendingTaManager: his("Pending TA Manager"),
  assigned: his("Assigned"),
});

/** The HR Business Partner's refusal of a hold, cancel or reopen request, on red (DC-18). */
export const STATE_CHANGE_DECISION = /* @__PURE__ */ defineLabels({
  rejected: his("Rejected"),
});

const COMP_CHAIN = "Chain \"Compensation: Pending -> In Review -> Finalized.\"";
export const COMPENSATION_REVIEW_STATUS = /* @__PURE__ */ defineLabels({
  pending: his("Pending"),
  inReview: his("In Review"),
  finalized: his("Finalized"),
});

const INTAKE_CHAIN = "\"Statuses / workflow: Intake Scheduled -> In Progress -> Draft Plan -> Validation Failed or Ready -> Finalized.\"";
export const INTAKE_STATUS = /* @__PURE__ */ defineLabels({
  intakeScheduled: his("Intake Scheduled"),
  inProgress: his("In Progress"),
  draftPlan: his("Draft Plan"),
  validationFailed: his("Validation Failed"),
  ready: his("Ready"),
  finalized: his("Finalized"),
});

const ASSESSMENT_CHAIN = "\"Statuses / workflow: Not Required or Required -> Draft -> Sent -> Submitted -> Reviewed -> Completed/Withdrawn.\"";
export const ASSESSMENT_STATUS = /* @__PURE__ */ defineLabels({
  notRequired: his("Not Required"),
  required: his("Required"),
  draft: his("Draft"),
  sent: his("Sent"),
  submitted: his("Submitted"),
  reviewed: his("Reviewed"),
  completed: his("Completed"),
  withdrawn: his("Withdrawn"),
});

export const PROFILE_STATUS = /* @__PURE__ */ defineLabels({
  active: his("Active"),
  archived: his("Archived"),
  merged: his("Merged"),
});

export const APPLICATION_STATUS = /* @__PURE__ */ defineLabels({
  sourced: his("Sourced"),
  qualified: his("Qualified"),
  dispositioned: his("Dispositioned"),
});

const SLATE_CHAIN = "\"Statuses / workflow: Draft Batch -> Submitted -> In HM Review -> Completed/Overdue.\"";
export const SLATE_STATUS = /* @__PURE__ */ defineLabels({
  draftBatch: his("Draft Batch"),
  submitted: his("Submitted"),
  inHmReview: his("In HM Review"),
  completed: his("Completed"),
  overdue: his("Overdue"),
});

const HM_REVIEW_CHAIN = "\"Statuses / workflow: Pending Review -> Advanced/Held/Passed. Held remains active with age and reason.\"";
export const HM_REVIEW_STATUS = /* @__PURE__ */ defineLabels({
  pendingReview: his("Pending Review"),
  advanced: his("Advanced"),
  held: his("Held"),
  passed: his("Passed"),
});

const SCREEN_CHAIN = "\"Statuses / workflow: Screen Requested -> Scheduled -> Completed -> Advance/Hold/Pass/Withdrawn.\"";
export const SCREEN_STATUS = /* @__PURE__ */ defineLabels({
  screenRequested: his("Screen Requested"),
  scheduled: his("Scheduled"),
  completed: his("Completed"),
  advance: his("Advance"),
  hold: his("Hold"),
  pass: his("Pass"),
  withdrawn: his("Withdrawn"),
});

const SELECTION_CHAIN = "\"Statuses / workflow: Pending HM Selection -> Advance/Hold/Pass. Advanced -> HM Interview Scheduling Requested.\"";
export const HM_SELECTION_STATUS = /* @__PURE__ */ defineLabels({
  pendingHmSelection: his("Pending HM Selection"),
  advance: his("Advance"),
  hold: his("Hold"),
  pass: his("Pass"),
  advanced: his("Advanced"),
  hmInterviewSchedulingRequested: his("HM Interview Scheduling Requested"),
});

const DECISION_CHAIN = "\"Candidate decision Pending -> Advance/Hold/Pass.\"";
export const CANDIDATE_DECISION_STATUS = /* @__PURE__ */ defineLabels({
  pending: his("Pending"),
  advance: his("Advance"),
  hold: his("Hold"),
  pass: his("Pass"),
});

const SCHEDULING_CHAIN = "\"Statuses: Requested, Scheduling in Progress, Awaiting Candidate Availability, Awaiting Interviewer Availability, Scheduled, Reschedule Requested, Completed, Cancelled.\"";

/** The eight Workbench statuses (BRD §4.2). The §4.2 spellings wait on G §23.6 and are drawn pending. */
export const SCHEDULING_STATUS = /* @__PURE__ */ defineLabels({
  requested: his("Requested"),
  schedulingInProgress: pending("Scheduling in Progress"),
  awaitingCandidateAvailability: pending("Awaiting Candidate Availability"),
  awaitingInterviewerAvailability: pending("Awaiting Interviewer Availability"),
  scheduled: his("Scheduled"),
  rescheduleRequested: his("Reschedule Requested"),
  completed: his("Completed"),
  cancelled: his("Cancelled"),
});

const LOOP_CHAIN = "\"Ready to Schedule -> In Progress -> Awaiting Availability -> Scheduled -> Completed; Blocked for panel/coverage\"";
export const FINAL_LOOP_SCHEDULING_STATUS = /* @__PURE__ */ defineLabels({
  readyToSchedule: his("Ready to Schedule"),
  inProgress: his("In Progress"),
  awaitingAvailability: his("Awaiting Availability"),
  scheduled: his("Scheduled"),
  completed: his("Completed"),
  blocked: his("Blocked for panel/coverage"),
});

const PACKET_CHAIN = "\"Statuses / workflow: Packet Draft -> Generated -> Reviewed -> Released.\"";
export const PACKET_STATUS = /* @__PURE__ */ defineLabels({
  packetDraft: his("Packet Draft"),
  generated: his("Generated"),
  reviewed: his("Reviewed"),
  released: his("Released"),
});

const ACK_CHAIN = "Chain \"Acknowledgment Pending -> Accepted/Declined.\"";
export const ACKNOWLEDGMENT_STATUS = /* @__PURE__ */ defineLabels({
  acknowledgmentPending: his("Acknowledgment Pending"),
  accepted: his("Accepted"),
  declined: his("Declined"),
});

/** A seat left unanswered past the threshold: one word for the AckPair pill, the Responses pill and the avatar ring. */
export const RESPONSE_STATUS = /* @__PURE__ */ defineLabels({
  nonresponse: his("Nonresponse"),
});

export const PANEL_CHANGE_STATUS = /* @__PURE__ */ defineLabels({
  draft: his("Draft"),
  submitted: his("Submitted"),
  pendingRecruiterReview: his("Pending Recruiter Review"),
  pendingHmReview: his("Pending HM Review"),
  blocked: his("Blocked"),
  approved: his("Approved"),
  rejected: his("Rejected"),
  communicated: his("Communicated"),
  completed: his("Completed"),
});

const INTERVIEW_CHAIN = "chain \"Interview Scheduled -> Completed -> Scorecard Due -> Submitted/Past Due.\"";
export const INTERVIEW_STATUS = /* @__PURE__ */ defineLabels({
  interviewScheduled: his("Interview Scheduled"),
  completed: his("Completed"),
  scorecardDue: his("Scorecard Due"),
  submitted: his("Submitted"),
  pastDue: his("Past Due"),
});

const SCORECARD_CHAIN = "\"Statuses / workflow: Not Started -> In Progress -> Submitted/Past Due.\"";
export const SCORECARD_STATUS = /* @__PURE__ */ defineLabels({
  notStarted: his("Not Started"),
  inProgress: his("In Progress"),
  submitted: his("Submitted"),
  pastDue: his("Past Due"),
  waived: his("Waived"),
});

export const DEBRIEF_READINESS_STATUS = /* @__PURE__ */ defineLabels({
  ready: his("Ready"),
  notReady: his("Not Ready"),
});

const SUMMARY_CHAIN = "\"Statuses / workflow: Waiting for Inputs -> Compiling -> Recruiter Review -> Ready for Debrief or Blocked.\"";
export const SUMMARY_STATUS = /* @__PURE__ */ defineLabels({
  waitingForInputs: his("Waiting for Inputs"),
  compiling: his("Compiling"),
  recruiterReview: his("Recruiter Review"),
  readyForDebrief: his("Ready for Debrief"),
  blocked: his("Blocked"),
});

const DEBRIEF_CHAIN = "\"Statuses / workflow: Not Ready -> Scheduled -> Roll Call -> Reveal -> Discussion -> Alignment -> Outcome Recorded/Blocked.\"";
export const DEBRIEF_STATUS = /* @__PURE__ */ defineLabels({
  notReady: his("Not Ready"),
  scheduled: his("Scheduled"),
  rollCall: his("Roll Call"),
  reveal: his("Reveal"),
  discussion: his("Discussion"),
  alignment: his("Alignment"),
  outcomeRecorded: his("Outcome Recorded"),
  blocked: his("Blocked"),
});

/**
 * The recommendation words (C5, DC-245, DC-247): the playbook's four (Strong Hire · Hire · Consider Other Role · No
 * Hire, his 13 Oct Corporate Interview Playbook, page 7) and the earlier scale's middle option, shown only on a card
 * recorded with it and always with the earlier-scale marker. This module is the one frontend place that spells the
 * earlier option; every screen reads it through recommendationWord().
 */
const PLAYBOOK_RECO = "**Strong Hire** · **Hire** · **Consider Other Role** · **No Hire**";
export const RECOMMENDATION_LABELS = /* @__PURE__ */ defineLabels({
  strongHire: his("Strong Hire"),
  hire: his("Hire"),
  considerOtherRole: his("Consider Other Role"),
  noHire: his("No Hire"),
  hireWithReservations: his("Hire with Reservations"),
  earlierScale: his("earlier scale"),
});

/** The stored recommendation values, in the stored order (enums.RECOMMENDATIONS, append only). */
export const RECOMMENDATION_VALUES = ["hire", "hire_with_reservations", "no_hire", "strong_hire", "consider_other_role"] as const;
export type RecommendationValue = (typeof RECOMMENDATION_VALUES)[number];

/** A recommendation's tone (DC-245, DC-18): green strong form, green, orange (a concern), red, gold (earlier scale). */
export type RecoTone = "ok-strong" | "ok" | "concern" | "bad" | "wait";

/** Each stored value's client key in RECOMMENDATION_LABELS (C5). */
const RECOMMENDATION_KEY: Readonly<Record<RecommendationValue, keyof typeof RECOMMENDATION_LABELS>> = {
  strong_hire: "strongHire", hire: "hire", consider_other_role: "considerOtherRole", no_hire: "noHire",
  hire_with_reservations: "hireWithReservations",
};

/** The tone of each stored value (C5). */
export const RECOMMENDATION_TONES: Readonly<Record<RecommendationValue, RecoTone>> = {
  strong_hire: "ok-strong", hire: "ok", consider_other_role: "concern", no_hire: "bad", hire_with_reservations: "wait",
};

export interface RecommendationWord {
  label: string;
  tone: RecoTone;
  /** The card was recorded on the earlier scale: draw the earlier-scale marker beside the label. */
  earlierScale: boolean;
}

const isRecommendationValue = (value: string): value is RecommendationValue =>
  (RECOMMENDATION_VALUES as readonly string[]).includes(value);

/** The word, tone and earlier-scale marker of a stored recommendation; an unknown value is null, never a guess. */
export function recommendationWord(value: string | null | undefined): RecommendationWord | null {
  if (value === null || value === undefined || !isRecommendationValue(value)) return null;
  return {
    label: RECOMMENDATION_LABELS[RECOMMENDATION_KEY[value]],
    tone: RECOMMENDATION_TONES[value],
    earlierScale: value === "hire_with_reservations",
  };
}

export const DEBRIEF_OUTCOME_LABELS = /* @__PURE__ */ defineLabels({
  primary: his("Primary"),
  alternates: his("Alternates"),
  continue: his("Continue"),
  reopen: his("Reopen"),
  additionalInterviewException: his("Additional-interview exception"),
  dispositionOthers: his("Disposition others"),
});

const REFERENCE_CHAIN = "\"Statuses / workflow: Not Required or Required -> Requested -> In Progress -> Completed/Unable to Complete.\"";
export const REFERENCE_STATUS = /* @__PURE__ */ defineLabels({
  notRequired: his("Not Required"),
  required: his("Required"),
  requested: his("Requested"),
  inProgress: his("In Progress"),
  completed: his("Completed"),
  unableToComplete: his("Unable to Complete"),
});

const PACKAGE_CHAIN = "\"Statuses / workflow: Draft -> Pending Approvals -> Ready for Verbal Offer or Rejected/Revision Required.\"";
export const OFFER_PACKAGE_STATUS = /* @__PURE__ */ defineLabels({
  draft: his("Draft"),
  pendingApprovals: his("Pending Approvals"),
  readyForVerbalOffer: his("Ready for Verbal Offer"),
  rejected: his("Rejected"),
  revisionRequired: his("Revision Required"),
});

const OFFER_CHAIN = "Chain \"Approved -> Verbal Extended -> Written Sent -> Awaiting Candidate -> Negotiation -> Revised Offer -> Accepted/Declined.\"";
export const OFFER_STATUS = /* @__PURE__ */ defineLabels({
  approved: his("Approved"),
  verbalExtended: his("Verbal Extended"),
  writtenSent: his("Written Sent"),
  awaitingCandidate: his("Awaiting Candidate"),
  negotiation: his("Negotiation"),
  revisedOffer: his("Revised Offer"),
  accepted: his("Accepted"),
  declined: his("Declined"),
});

/** Req health's three bands (BRD §4.4); the score and the drivers are the health component's. */
export const HEALTH_BAND_LABELS = /* @__PURE__ */ defineLabels({
  green: his("Green"),
  yellow: his("Yellow"),
  red: his("Red"),
});

// ---- templates -----------------------------------------------------------------------------------------------------

export const LABEL_TEMPLATES = {
  dueIn: /* @__PURE__ */ template(his("Due in {n} business days")),
  heldBy: /* @__PURE__ */ template(his("Held by {actor} · {rel}")),
  archivedOn: /* @__PURE__ */ template(his("Archived {date} · searchable, reportable, auditable, reopenable")),
  recordedReferenceCheck: /* @__PURE__ */ template(pending("{actor} recorded the reference check · {status}")),
  finalLoopRound: /* @__PURE__ */ template(pending("Final Interview Loop · Round {n}")),
  packetRevoked: /* @__PURE__ */ template(pending("Access to this packet was revoked on {date}")),
} as const;
